import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Select as MuiSelect,
  TextField,
  Typography,
} from "@mui/material";
import { usePermissions } from "../../context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import Toast from "../../components/Toast";
import { initiateOnboarding } from "../../services/vipVendorOnboarding";
import { readErrorMessage } from "./utils/mapBeErrors";
import { COUNTRY_CODES } from "./fields/PhoneField";
import { PHONE_RULES, validatePhone } from "./utils/schemaUtils";

const DEFAULT_MAX_LENGTH = 15;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NAME_MIN_LENGTH = 2;
// 140 aligns with ISO 20022 payment-message `Name` and SWIFT counterparty
// name — the outer bound for legitimate international vendor names. Indian
// GST-registered names cap around 99, PAN-verified names around 60; both
// fit comfortably.
const NAME_MAX_LENGTH = 140;
// Unicode letters + digits + spaces + normal business-name punctuation:
//   . , ' & / ( ) - _
// Rejects @ # $ % ^ * = < > " : ; \ etc. — the noise QA sees on the invite screen.
const NAME_PATTERN = /^[\p{L}\p{N}\s.,'&/()\-_]+$/u;

type FieldErrors = Partial<Record<"name" | "email" | "mobile", string>>;

type ToastItem = {
  id: string;
  text: string;
  type: "success" | "error";
  autoClose: boolean;
};

function validate(input: {
  name: string;
  email: string;
  mobile: string;
  mobile_country_code: string;
}): FieldErrors {
  const errors: FieldErrors = {};
  const trimmedName = input.name.trim();
  if (!trimmedName) {
    errors.name = "Name is required";
  } else if (trimmedName.length < NAME_MIN_LENGTH) {
    errors.name = `Name must be at least ${NAME_MIN_LENGTH} characters`;
  } else if (trimmedName.length > NAME_MAX_LENGTH) {
    errors.name = `Name must be ${NAME_MAX_LENGTH} characters or fewer`;
  } else if (!NAME_PATTERN.test(trimmedName)) {
    errors.name =
      "Name can only contain letters, numbers, spaces, and . , ' & / ( ) - _";
  }
  if (!input.email.trim()) {
    errors.email = "Email is required";
  } else if (!EMAIL_PATTERN.test(input.email.trim())) {
    errors.email = "Enter a valid email address";
  }
  if (!input.mobile) {
    errors.mobile = "Mobile number is required";
  } else {
    const phoneErr = validatePhone(input.mobile, input.mobile_country_code);
    if (phoneErr) errors.mobile = phoneErr;
  }
  return errors;
}

function InviteVendor() {
  const { hasPermission } = usePermissions();
  const authorized = hasPermission(BOUNCER_PERMISSIONS.VENDOR_ONBOARD);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [mobileCountryCode, setMobileCountryCode] = useState("+91");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [toastList, setToastList] = useState<ToastItem[]>([]);

  const mobileMaxLength =
    PHONE_RULES[mobileCountryCode]?.length ?? DEFAULT_MAX_LENGTH;

  const pushToast = (text: string, type: "success" | "error") => {
    setToastList((list) => [
      ...list,
      { id: `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, text, type, autoClose: true },
    ]);
  };

  if (!authorized) {
    return (
      <Box sx={{ maxWidth: 720, mx: "auto", mt: 8, p: 3 }}>
        <Alert severity="error">
          You don&apos;t have permission to invite vendors. Contact your admin
          if you believe this is a mistake.
        </Alert>
      </Box>
    );
  }

  const resetForm = () => {
    setName("");
    setEmail("");
    setMobile("");
    setMobileCountryCode("+91");
    setFieldErrors({});
  };

  const handleCountryCodeChange = (nextCode: string) => {
    setMobileCountryCode(nextCode);
    const nextMax = PHONE_RULES[nextCode]?.length ?? DEFAULT_MAX_LENGTH;
    setMobile((prev) => prev.slice(0, nextMax));
    if (fieldErrors.mobile) {
      setFieldErrors((errs) => ({ ...errs, mobile: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = {
      name: name.trim(),
      email: email.trim(),
      mobile,
      mobile_country_code: mobileCountryCode,
    };
    const clientErrors = validate(trimmed);
    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      await initiateOnboarding(trimmed);
      pushToast(`Invitation email sent to ${trimmed.email}.`, "success");
      resetForm();
    } catch (err) {
      pushToast(
        readErrorMessage(err, "We couldn't send the invite. Please try again."),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", mt: 8, p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 4, sm: 6 },
          border: 1,
          borderColor: "#E0E4EF",
          borderRadius: "12px",
        }}
      >
        <Typography
          sx={{ fontSize: 26, fontWeight: 700, mb: 1, textAlign: "center" }}
        >
          Invite a Vendor
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 4, textAlign: "center" }}
        >
          Enter the vendor&apos;s name, email, and mobile number.
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, NAME_MAX_LENGTH))}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || " "}
            autoFocus
            fullWidth
            disabled={submitting}
            inputProps={{
              "aria-label": "Name",
              maxLength: NAME_MAX_LENGTH,
            }}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email || " "}
            fullWidth
            disabled={submitting}
            inputProps={{ "aria-label": "Email" }}
          />
          <Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <MuiSelect
                value={mobileCountryCode}
                onChange={(e) => handleCountryCodeChange(e.target.value as string)}
                disabled={submitting}
                size="medium"
                variant="outlined"
                inputProps={{ "aria-label": "Country code" }}
                sx={{ width: 140, flexShrink: 0 }}
              >
                {COUNTRY_CODES.map((cc) => (
                  <MenuItem key={cc.code} value={cc.code}>
                    {cc.label}
                  </MenuItem>
                ))}
              </MuiSelect>
              <TextField
                label="Mobile number"
                type="tel"
                value={mobile}
                onChange={(e) =>
                  setMobile(
                    e.target.value.replace(/\D/g, "").slice(0, mobileMaxLength),
                  )
                }
                error={Boolean(fieldErrors.mobile)}
                fullWidth
                disabled={submitting}
                inputProps={{
                  inputMode: "numeric",
                  maxLength: mobileMaxLength,
                  "aria-label": "Mobile number",
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: fieldErrors.mobile ? "error.main" : "text.secondary",
                mt: 0.5,
                ml: 1.5,
                display: "block",
                minHeight: "1.25em",
              }}
            >
              {fieldErrors.mobile || " "}
            </Typography>
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            size="large"
            sx={{ mt: 2, alignSelf: "center", px: 6 }}
          >
            {submitting ? "Sending invite…" : "Send invite"}
          </Button>
        </Box>
      </Paper>

      <Toast toastList={toastList} setToastList={setToastList} />
    </Box>
  );
}

export default InviteVendor;
